import { useState } from "react";
import intl from "react-intl-universal";
import { ValutField, ValutInput } from "../../Atoms/ValutInput";
import { DateRangeCalendar } from "../GridHistory";

export const PanelActions = ({ onCancel, onApply }) => (
  <div className="extract-filter-panel__actions">
    <button type="button" className="extract-filter-panel__btn" onClick={onCancel}>
      {intl.get("common.cancel")}
    </button>
    <button
      type="button"
      className="extract-filter-panel__btn extract-filter-panel__btn--primary"
      onClick={onApply}
    >
      {intl.get("common.apply")}
    </button>
  </div>
);

export const DateFilterPanel = ({ draft, onChange, dateFromKey = "dateFrom", dateToKey = "dateTo" }) => (
  <div className="extract-filter-panel__body">
    <DateRangeCalendar
      dateFrom={draft[dateFromKey]}
      dateTo={draft[dateToKey]}
      onChange={({ dateFrom, dateTo }) => onChange({ ...draft, [dateFromKey]: dateFrom, [dateToKey]: dateTo })}
    />
  </div>
);

export const AmountFilterPanel = ({
  draft,
  onChange,
  amountMinKey = "amountMin",
  amountMaxKey = "amountMax",
  minLabelKey = "extract.amountMin",
  maxLabelKey = "extract.amountMax",
  idPrefix = "modern-filter",
}) => (
  <div className="extract-filter-panel__body">
    <div className="grid gap-3 sm:grid-cols-2">
      <ValutField label={intl.get(minLabelKey)} htmlFor={`${idPrefix}-amount-min`}>
        <ValutInput
          id={`${idPrefix}-amount-min`}
          inputMode="decimal"
          placeholder="0,00"
          value={draft[amountMinKey]}
          onChange={(event) => onChange({ ...draft, [amountMinKey]: event.target.value })}
        />
      </ValutField>
      <ValutField label={intl.get(maxLabelKey)} htmlFor={`${idPrefix}-amount-max`}>
        <ValutInput
          id={`${idPrefix}-amount-max`}
          inputMode="decimal"
          placeholder="0,00"
          value={draft[amountMaxKey]}
          onChange={(event) => onChange({ ...draft, [amountMaxKey]: event.target.value })}
        />
      </ValutField>
    </div>
  </div>
);

export const CheckboxListFilterPanel = ({
  options = [],
  selectedValues = [],
  onChange,
  idPrefix = "modern-filter",
  getOptionIcon,
  singleSelect = false,
  searchable = false,
  searchPlaceholder,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const selectedCount = selectedValues.length;
  const handleClear = () => onChange([]);

  const handleToggle = (optionId) => {
    if (singleSelect) {
      onChange(selectedValues.includes(optionId) ? [] : [optionId]);
      return;
    }
    const next = selectedValues.includes(optionId)
      ? selectedValues.filter((value) => value !== optionId)
      : [...selectedValues, optionId];
    onChange(next);
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleOptions =
    searchable && normalizedQuery
      ? options.filter((option) =>
          String(option.label || "")
            .toLowerCase()
            .includes(normalizedQuery),
        )
      : options;

  return (
    <div className="extract-filter-panel__body">
      {searchable ? (
        <div className="mb-3">
          <ValutInput
            id={`${idPrefix}-search`}
            type="search"
            value={searchQuery}
            placeholder={searchPlaceholder || "Search…"}
            onChange={(event) => setSearchQuery(event.target.value)}
            aria-label={searchPlaceholder || "Search"}
          />
        </div>
      ) : null}

      {selectedCount > 0 && (
        <div className="extract-filter-panel__selection-bar">
          <span className="extract-filter-panel__selection-count">
            {intl.get("extract.selectedCount", { count: selectedCount })}
          </span>
          <button type="button" className="extract-filter-panel__clear-link" onClick={handleClear}>
            {intl.get("words.clear")}
          </button>
        </div>
      )}

      <div className="extract-filter-panel__options">
        {visibleOptions.map((option) => {
          const OptionIcon = getOptionIcon?.(option);
          const isChecked = selectedValues.includes(option.id);
          return (
            <label
              key={option.id}
              htmlFor={`${idPrefix}-${option.id}`}
              className="extract-filter-option"
            >
              <input
                id={`${idPrefix}-${option.id}`}
                type="checkbox"
                checked={isChecked}
                onChange={() => handleToggle(option.id)}
                className="extract-filter-option__checkbox"
              />
              {OptionIcon ? (
                <span
                  className={`extract-filter-option__icon extract-filter-option__icon--${option.id}`}
                >
                  <OptionIcon aria-hidden />
                </span>
              ) : null}
              <span className="extract-filter-option__label">{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const MoreFiltersDateSection = ({
  draft,
  onChange,
  dateFromKey = "dateFrom",
  dateToKey = "dateTo",
  label = intl.get("extract.filterDate"),
}) => (
  <section className="extract-more-filters-form__section extract-more-filters-form__section--date">
    <p className="extract-more-filters-form__label">{label}</p>
    <DateRangeCalendar
      variant="modal"
      showDateInputs
      dateFrom={draft[dateFromKey]}
      dateTo={draft[dateToKey]}
      onChange={({ dateFrom, dateTo }) => onChange({ ...draft, [dateFromKey]: dateFrom, [dateToKey]: dateTo })}
    />
  </section>
);

export const MoreFiltersAmountSection = ({
  draft,
  onChange,
  amountMinKey = "amountMin",
  amountMaxKey = "amountMax",
  minLabelKey = "extract.amountMin",
  maxLabelKey = "extract.amountMax",
  idPrefix = "modern-more",
}) => (
  <section className="extract-more-filters-form__section">
    <p className="extract-more-filters-form__label">{intl.get("extract.filterAmount")}</p>
    <div className="extract-more-filters-form__amount-grid">
      <ValutField label={intl.get(minLabelKey)} htmlFor={`${idPrefix}-amount-min`}>
        <ValutInput
          id={`${idPrefix}-amount-min`}
          inputMode="decimal"
          placeholder="0,00"
          value={draft[amountMinKey]}
          onChange={(event) => onChange({ ...draft, [amountMinKey]: event.target.value })}
        />
      </ValutField>
      <ValutField label={intl.get(maxLabelKey)} htmlFor={`${idPrefix}-amount-max`}>
        <ValutInput
          id={`${idPrefix}-amount-max`}
          inputMode="decimal"
          placeholder="0,00"
          value={draft[amountMaxKey]}
          onChange={(event) => onChange({ ...draft, [amountMaxKey]: event.target.value })}
        />
      </ValutField>
    </div>
  </section>
);

export const MoreFiltersCheckboxSection = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  idPrefix = "modern-more",
  getOptionIcon,
}) => (
  <section className="extract-more-filters-form__section">
    <p className="extract-more-filters-form__label">{label}</p>
    <div className="extract-more-filters-form__type-options">
      {options.map((option) => {
        const OptionIcon = getOptionIcon?.(option);
        return (
          <label
            key={option.id}
            htmlFor={`${idPrefix}-${option.id}`}
            className="extract-filter-option"
          >
            <input
              id={`${idPrefix}-${option.id}`}
              type="checkbox"
              checked={selectedValues.includes(option.id)}
              onChange={() => {
                const next = selectedValues.includes(option.id)
                  ? selectedValues.filter((value) => value !== option.id)
                  : [...selectedValues, option.id];
                onChange(next);
              }}
              className="extract-filter-option__checkbox"
            />
            {OptionIcon ? (
              <span className={`extract-filter-option__icon extract-filter-option__icon--${option.id}`}>
                <OptionIcon aria-hidden />
              </span>
            ) : null}
            <span className="extract-filter-option__label">{option.label}</span>
          </label>
        );
      })}
    </div>
  </section>
);
